import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useAccountPositionStore } from '../../Store/accountPositionStore';
import { tokens } from '../../Common/designTokens'; // added token import

// Helper function to format currency values
const formatCurrency = (value) => {
  if (value === "---") return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return "---";
  // Format to 5 decimals, comma separated, remove trailing zeros after decimal
  let formatted = numValue.toLocaleString('en-US', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5
  }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return `$${formatted}`;
};

// Helper function to format percentage values - always with decimal places
const formatPercent = (value) => {
  if (value === "---") return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return value;
  let formatted = numValue.toLocaleString('en-US', {
    minimumFractionDigits: 5,
    maximumFractionDigits: 5
  }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return `${formatted}%`;
};

// Helper function to format numeric values based on their type
const formatNumeric = (value, decimals = null) => {
  if (value === "---") return value;
  const numValue = Number(value);
  if (isNaN(numValue)) return "---";
  const isInteger = decimals === null ? Number.isInteger(numValue) : false;
  const decimalPlaces = decimals !== null ? decimals : (isInteger ? 0 : 5);
  let formatted = numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return formatted;
};

// helper to produce font color with alpha (tokens.colors.fontColour is rgba(...,1))
const fontWithAlpha = (alpha) =>
  tokens.colors.fontColour.replace(/1\)$/, `${alpha})`);

// helper to produce white with alpha if needed
const whiteWithAlpha = (alpha) =>
  tokens.colors.white.replace(/1\)$/, `${alpha})`);

const SummaryItem = ({ label, value }) => (
   <Box sx={{ 
    // width, minWidth, maxWidth removed to let grid control sizing
    height: 60,
    borderRadius: '8px', // <-- changed from '4px'
    border: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced hardcoded rgba
    padding: {
      xs: '0px 0px', // default/mobile
      lg: '0px 0px', // reduced horizontal padding for laptop and up
      xl: '0px 0px'
    },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  }}>
    <Typography 
      variant="caption" 
      sx={{ 
        fontSize: '14px', 
        color: fontWithAlpha(0.5), // replaced rgba with token helper
        textAlign: 'center', 
        lineHeight: 1.2 
      }}
    >
      {label}
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        fontSize: '14px', 
        color: tokens.colors.fontColour, // replaced white with theme font color
        fontWeight: 'medium', 
        lineHeight: 1.2, 
        textAlign: 'center' 
      }}
    >
      {value}
    </Typography>
  </Box>
);

const gridTemplateColumns = {
  xs: 'repeat(1, 1fr)',   // 1 column on extra small screens
  sm: 'repeat(2, 1fr)',   // 2 columns on small screens
  md: 'repeat(3, 1fr)',   // 3 columns on medium screens
  lg: 'repeat(6, 1fr)',   // 6 columns on large screens and up
};

const SummaryGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: gridTemplateColumns,
      gap: '16px',
      mb: '16px',                 // keep default gap between rows
      '&:last-of-type': {        // remove margin after the last grid
        mb: 0,
      },
      width: '100%',
    }}
  >
    {children}
  </Box>
);

const AccountSummary = ({ accounts = [], selectedAccount }) => {
  const { accountSummary } = useAccountPositionStore();

  // Find the selected account from the accounts list
  const selectedAccountObj = accounts.find(acc => acc.id === selectedAccount) || {};

  // Helper to get fallback value from account list if summary is missing
  const getSummaryOrAccount = (summaryValue, accountValue) => {
    if (summaryValue === null || summaryValue === undefined || summaryValue === '') {
      return accountValue || "---";
    }
    return summaryValue;
  };

  // Debug print
  // console.log('accountSummary:', accountSummary);

  // Handle null or undefined values in summary data
  const getSafeValue = (value) => {
    // Only return "---" for null, undefined, or empty string
    // Allow 0, negative numbers, and positive numbers to pass through
    if (value === null || value === undefined || value === '') {
      return "---";
    }
    // For string values (like ratings), return as-is if not empty
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
    // For all numeric values (including 0 and negatives), return as-is
    return value;
  };
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' }, // Stack on mobile, row on laptop+
            flexWrap: { xs: 'wrap', lg: 'nowrap' },     // Allow wrap on mobile, no wrap on laptop+
            alignItems: { xs: 'flex-start', lg: 'center' },
            gap: '16px'
          }}
        >
          <Typography variant="h6" sx={{ color: tokens.colors.fontColour, fontWeight: 'bold', textAlign: 'left', mb: 0.5 }}>
            Account Summary
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: tokens.colors.fontColour85, // replaced rgba with token
              fontWeight: 700,
              fontSize: '14px',
              whiteSpace: 'pre-line'
            }}
          >
            {/* Name: {getSummaryOrAccount(accountSummary.userName, selectedAccountObj.userName)} &nbsp; | &nbsp; */}
            Account #: {getSummaryOrAccount(accountSummary.accountIdentifier, selectedAccountObj.accountNumber || selectedAccountObj.accountIdentifier)} &nbsp; | &nbsp;
            Account Description: {getSummaryOrAccount(accountSummary.accountDescription, selectedAccountObj.description)} &nbsp; | &nbsp;
            Custodian: {getSummaryOrAccount(accountSummary.custodianName, selectedAccountObj.custodianName)}
          </Typography>
        </Box>
      </Box>
      
      {/* Responsive Grid - First 6 items */}
      <SummaryGrid>
        <SummaryItem 
          label="Total Par Value" 
          value={formatCurrency(getSafeValue(accountSummary.totalPar))} 
        />
        <SummaryItem 
          label="Total Current Value" 
          value={formatCurrency(getSafeValue(accountSummary.totalCurrValue))} 
        />
        <SummaryItem 
          label="Avg Yield to Worst" 
          value={formatNumeric(getSafeValue(accountSummary.averageYieldToWorst), 4)} 
        />
        <SummaryItem 
          label="Avg Yield to Maturity" 
          value={formatNumeric(getSafeValue(accountSummary.avgYieldToMaturity), 4)} 
        />
        <SummaryItem 
          label="Avg $ Duration" // <-- changed label
          value={formatNumeric(getSafeValue(accountSummary.average), 4)} 
        />
        <SummaryItem 
          label="Convexity" 
          value={formatNumeric(getSafeValue(accountSummary.convexity), 4)} 
        />
      </SummaryGrid>
      
      {/* Responsive Grid - Second 6 items */}
      <SummaryGrid>
        <SummaryItem 
          label="Next 12M Principal " 
          value={formatCurrency(getSafeValue(accountSummary.next12MonthPrincipal))} 
        />
        <SummaryItem 
          label="Next 12M Interest " 
          value={formatCurrency(getSafeValue(accountSummary.next12MonthInterest))} 
        />
        <SummaryItem 
          label="Avg S&P/Moody's Rating " 
          value={
            `${getSafeValue(accountSummary.averageSPRating)}/${getSafeValue(accountSummary.averageMoodyRating)}` || "---"
          } 
        />
        <SummaryItem 
          label="DV01" 
          value={getSafeValue(accountSummary.totalDv01)} 
        />
        <SummaryItem 
          label="Total Non Taxable" 
          value={formatCurrency(getSafeValue(accountSummary.nonTaxable))} 
        />
        <SummaryItem 
          label="Total Taxable" 
          value={formatCurrency(getSafeValue(accountSummary.taxable))} 
        />
      </SummaryGrid>
    </Box>
  );
};

export default AccountSummary;
